import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { CheckoutForm, CheckoutFormData } from '../components/checkout/CheckoutForm';
import { useCart } from '../lib/cart';
import { supabase } from '../lib/supabase';
import { formatCurrency } from '../lib/utils';
import { ArrowLeft, Store, ShoppingBag } from 'lucide-react';
import { EmptyState } from '../components/ui/EmptyState';
import { Restaurant, RestaurantSettings } from '../lib/types';
export function CheckoutPage() {
  const navigate = useNavigate();
  const {
    cart,
    getTotal,
    clearCart
  } = useCart();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [settings, setSettings] = useState<RestaurantSettings | null>(null);
  useEffect(() => {
    if (cart.restaurantId) {
      // Fetch restaurant for payment method flags and express_fee
      supabase.from('restaurants').select('*').eq('id', cart.restaurantId).single().then(({
        data
      }) => {
        if (data) setRestaurant(data);
      });
      supabase.from('restaurant_settings').select('*').eq('restaurant_id', cart.restaurantId).single().then(({
        data
      }) => {
        if (data) setSettings(data);
      });
    }
  }, [cart.restaurantId]);
  const subtotal = getTotal();
  const deliveryFee = restaurant?.express_fee || 0;
  const total = subtotal + deliveryFee;
  const handleSubmit = async (data: CheckoutFormData) => {
    if (!cart.restaurantId) return;
    setIsSubmitting(true);
    try {
      // generate_order_number() is handled by Supabase, but we generate tracking_token client-side
      const trackingToken = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      const actualDeliveryFee = data.delivery_type === 'delivery' ? deliveryFee : 0;
      const orderTotal = subtotal + actualDeliveryFee;
      // Sanitize table_id: only pass if it looks like a valid UUID, otherwise null
      const sanitizedTableId = data.table_id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(data.table_id.trim()) ? data.table_id.trim() : null;
      // 1. Create Order (order_number generated by Supabase trigger)
      const {
        data: order,
        error: orderError
      } = await supabase.from('orders').insert({
        restaurant_id: cart.restaurantId,
        tracking_token: trackingToken,
        customer_name: data.customer_name,
        customer_phone: data.customer_phone,
        delivery_type: data.delivery_type,
        payment_method: data.payment_method,
        delivery_address: data.delivery_address || null,
        delivery_lat: data.delivery_lat || null,
        delivery_lng: data.delivery_lng || null,
        maps_url: data.maps_url || null,
        table_id: sanitizedTableId,
        notes: data.notes || null,
        subtotal,
        delivery_fee: actualDeliveryFee,
        total: orderTotal,
        status: 'new',
        source: 'web'
      }).select().single();
      if (orderError) {
        console.error('[NOIZA] orders insert error:', orderError);
        throw orderError;
      }
      // 2. Create Order Items & Options
      for (const item of cart.items) {
        const optionsTotal = item.selected_options.reduce((sum, opt) => sum + opt.price_delta, 0);
        const lineTotal = (item.unit_price_snapshot + optionsTotal) * item.quantity;
        const {
          data: orderItem,
          error: itemError
        } = await supabase.from('order_items').insert({
          order_id: order.id,
          restaurant_id: cart.restaurantId,
          menu_item_id: item.menu_item_id,
          item_name_snapshot: item.item_name_snapshot,
          unit_price_snapshot: item.unit_price_snapshot,
          quantity: item.quantity,
          notes: item.notes || null,
          station_code: null,
          line_total: lineTotal,
          status: 'pending'
        }).select().single();
        if (itemError) {
          console.error('[NOIZA] order_items insert error:', itemError, {
            item: item.item_name_snapshot
          });
          throw itemError;
        }
        if (item.selected_options.length > 0) {
          const optionsToInsert = item.selected_options.map(opt => ({
            order_item_id: orderItem.id,
            restaurant_id: cart.restaurantId,
            option_group: opt.option_group || '',
            option_name: opt.option_name,
            price_delta: opt.price_delta
          }));
          const {
            error: optionsError
          } = await supabase.from('order_item_options').insert(optionsToInsert);
          if (optionsError) {
            console.error('[NOIZA] order_item_options insert error:', optionsError, {
              item: item.item_name_snapshot
            });
            throw optionsError;
          }
        }
      }
      // 3. Create initial tracking event (non-blocking)
      const {
        error: trackingError
      } = await supabase.from('customer_tracking_events').insert({
        restaurant_id: cart.restaurantId,
        order_id: order.id,
        status: 'new',
        title: 'Pedido recibido',
        message: 'Tu pedido fue recibido correctamente y está esperando confirmación del restaurante.',
        visible_to_customer: true
      });
      if (trackingError) {
        console.error('[NOIZA] customer_tracking_events insert error:', trackingError);
      }
      // 4. Create order log (non-blocking)
      const {
        error: logError
      } = await supabase.from('order_logs').insert({
        restaurant_id: cart.restaurantId,
        order_id: order.id,
        event_type: 'order_created',
        event_payload: {
          source: 'web',
          delivery_type: data.delivery_type
        }
      });
      if (logError) {
        console.error('[NOIZA] order_logs insert error:', logError);
      }
      // 5. Generate and upload ticket image (non-blocking)
      const itemsSnapshot = [...cart.items];
      let ticketImageUrl: string | null = null;
      try {
        const {
          generateTicketImage,
          uploadTicketToCloudinary
        } = await import('../lib/ticket');
        const ticketBlob = await generateTicketImage({
          restaurantName: restaurant?.name || '',
          currencySymbol: restaurant?.currency_symbol || '₡',
          orderNumber: order.order_number,
          customerName: data.customer_name,
          customerPhone: data.customer_phone,
          deliveryType: data.delivery_type,
          deliveryAddress: data.delivery_address,
          mapsUrl: data.maps_url,
          items: itemsSnapshot,
          subtotal,
          deliveryFee: actualDeliveryFee,
          total: orderTotal,
          notes: data.notes,
          createdAt: order.created_at || new Date().toISOString()
        });
        const url = await uploadTicketToCloudinary(ticketBlob, order.order_number);
        if (url) {
          ticketImageUrl = url;
          // Save ticket URL to order (non-blocking)
          const {
            error: ticketUpdateError
          } = await supabase.from('orders').update({
            ticket_image_url: url
          }).eq('id', order.id);
          if (ticketUpdateError) {
            console.error('[NOIZA] ticket_image_url update error:', ticketUpdateError);
          }
        }
      } catch (ticketErr) {
        console.error('[NOIZA] Ticket generation/upload error:', ticketErr);
      }
      // Success! Clear cart and redirect
      clearCart();
      navigate('/order-confirmation', {
        state: {
          restaurant,
          orderNumber: order.order_number,
          trackingToken,
          customerName: data.customer_name,
          customerPhone: data.customer_phone,
          deliveryType: data.delivery_type,
          paymentMethod: data.payment_method,
          deliveryAddress: data.delivery_address,
          mapsUrl: data.maps_url,
          notes: data.notes,
          items: itemsSnapshot,
          subtotal,
          deliveryFee: actualDeliveryFee,
          total: orderTotal,
          ticketImageUrl
        }
      });
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Hubo un error al procesar tu pedido. Por favor, intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };
  if (cart.items.length === 0) {
    return <div className="min-h-screen flex flex-col bg-brand-offwhite" data-id="element-1113">
        <MarketplaceHeader data-id="element-1114" />
        <main className="flex-1 flex items-center justify-center py-12" data-id="element-1115">
          <EmptyState icon={ShoppingBag} title="Tu carrito está vacío" description="No puedes proceder al pago sin productos." actionLabel="Volver al inicio" onAction={() => navigate('/')} data-id="element-1116" />
        </main>
        <Footer data-id="element-1117" />
      </div>;
  }
  return <div className="min-h-screen flex flex-col bg-brand-offwhite" data-id="element-1118">
      <MarketplaceHeader data-id="element-1119" />

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8" data-id="element-1120">
        <div className="flex items-center gap-4 mb-8" data-id="element-1121">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-gray-400 hover:text-gray-900 rounded-full hover:bg-gray-50 transition-colors" data-id="element-1122">
            <ArrowLeft className="w-6 h-6" data-id="element-1123" />
          </button>
          <h1 className="text-3xl font-bold text-gray-900" data-id="element-1124">Finalizar Pedido</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-1125">
          {/* Main Form Area */}
          <div className="lg:col-span-8 order-2 lg:order-1" data-id="element-1126">
            <CheckoutForm restaurant={restaurant} settings={settings} onSubmit={handleSubmit} isLoading={isSubmitting} data-id="element-1127" />
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-4 order-1 lg:order-2" data-id="element-1128">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sticky top-24" data-id="element-1129">
              <div className="mb-6 pb-4 border-b border-gray-100" data-id="element-1130">
                <div className="flex items-center gap-3 mb-2" data-id="element-1131">
                  <Store className="w-5 h-5 text-gray-500" data-id="element-1132" />
                  <span className="font-bold text-gray-900" data-id="element-1133">
                    {cart.restaurantName}
                  </span>
                </div>
                <p className="text-xs text-gray-500" data-id="element-1134">
                  Tu pedido será enviado directamente al WhatsApp de este
                  restaurante.
                </p>
              </div>

              <h3 className="font-bold text-gray-900 mb-4" data-id="element-1135">
                Resumen del Pedido
              </h3>

              <div className="space-y-4 mb-6 max-h-[40vh] overflow-y-auto pr-2" data-id="element-1136">
                {cart.items.map(item => {
                const optionsTotal = item.selected_options.reduce((sum, opt) => sum + opt.price_delta, 0);
                const unitPrice = item.unit_price_snapshot + optionsTotal;
                const lineTotal = unitPrice * item.quantity;
                return <div key={item.id} className="flex justify-between text-sm" data-id="element-1137">
                      <div className="flex gap-2" data-id="element-1138">
                        <span className="font-medium text-gray-900" data-id="element-1139">
                          {item.quantity}x
                        </span>
                        <div data-id="element-1140">
                          <p className="text-gray-900" data-id="element-1141">
                            {item.item_name_snapshot}
                          </p>
                          {item.selected_options.length > 0 && <p className="text-xs text-gray-500 mt-0.5" data-id="element-1142">
                              {item.selected_options.map(o => o.option_name).join(', ')}
                            </p>}
                        </div>
                      </div>
                      <span className="font-medium text-gray-900" data-id="element-1143">
                        {formatCurrency(lineTotal)}
                      </span>
                    </div>;
              })}
              </div>

              <div className="space-y-3 text-sm text-gray-600 mb-6 border-t border-gray-100 pt-4" data-id="element-1144">
                <div className="flex justify-between" data-id="element-1145">
                  <span data-id="element-1146">Subtotal</span>
                  <span className="font-medium text-gray-900" data-id="element-1147">
                    {formatCurrency(subtotal)}
                  </span>
                </div>
                {deliveryFee > 0 && <div className="flex justify-between text-brand-amber" data-id="element-1148">
                    <span data-id="element-1149">Costo de envío</span>
                    <span className="font-medium" data-id="element-1150">
                      {formatCurrency(deliveryFee)}
                    </span>
                  </div>}
                <div className="pt-3 border-t border-gray-100 flex justify-between text-lg font-bold text-gray-900" data-id="element-1151">
                  <span data-id="element-1152">Total</span>
                  <span data-id="element-1153">{formatCurrency(total)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-1154" />
    </div>;
}